import "@liveblocks/react-ui/styles.css"
import "./CollaborationRoom.module.css"

import {
  LiveList,
  LiveObject,
  type LsonObject,
  type Room,
} from "@liveblocks/client"
import {
  LiveblocksProvider,
  RoomContext as LiveblocksRoomContext,
  useClient,
  useMutation,
  useStorage,
} from "@liveblocks/react"
import { useLiveblocksExtension } from "@liveblocks/react-tiptap"
import { getDocumentById } from "@prismicio/editor-fields"
import { use } from "@prismicio/editor-support/React"
import type { Content, Extension } from "@tiptap/core"
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import { queryClient } from "../../app/components/providers/QueryProvider"
import { env } from "../../app/env"
import {
  useCollaborationDataFlag,
  useCollaborationRoomFlag,
} from "../../app/integrations/AmplitudeExperiments"
import { getRepositoryName } from "../../app/routing"
import { baseUrl, type DocumentVersionStatus } from "../../app/services/core"
import {
  getLiveblocksSessionOptions,
  resolveMentionSuggestions,
  resolveUsers,
} from "../../app/services/liveblocks"
import { useRepository } from "../../app/services/repository"
import { useProfile } from "../../app/services/user"
import { type ExistingDocumentData, saveDocument } from "../documentData"
import { useDocumentHasLocalEdits } from "../DocumentHasLocalEditsProvider"
import { useExistingDocumentParams, useGoToDocument } from "../documentRouting"
import { findDocumentVersion } from "../findDocumentVersion"
import { CollaborationStateProvider } from "./CollaborationStateProvider"
import { handleDocumentStorageChanges } from "./handleDocumentStorageChanges"
import { useLiveblocksError } from "./LiveblocksErrorProvider"
import { RoomConnectionObserver } from "./RoomConnectionObserver"
import { wrapWithLiveblocksModel } from "./wrapWithLiveblocksModel"

export function CollaborationRoom(props: PropsWithChildren) {
  const { children } = props

  const { hasLocalEdits } = useDocumentHasLocalEdits()
  const hasCollaborationRoomTurnedOff = useCollaborationRoomFlag() === "off"

  const { authors } = useRepository()
  const { hasError, setError } = useLiveblocksError()

  async function authEndpoint() {
    try {
      return await queryClient.fetchQuery(getLiveblocksSessionOptions())
    } catch (err) {
      setError({
        type: "authentication",
        message: "Failed to authenticate with Liveblocks.",
      })
      throw err
    }
  }

  if (hasLocalEdits || hasCollaborationRoomTurnedOff || hasError) {
    return (
      <CollaborationStateProvider enabled={false}>
        {children}
      </CollaborationStateProvider>
    )
  }

  const aiAuthor = {
    id: "ai@prismic.io",
    email: "ai@prismic.io",
    firstname: "AI",
    lastname: "Assistant",
  }

  return (
    <CollaborationStateProvider enabled>
      <LiveblocksProvider
        largeMessageStrategy="split"
        authEndpoint={authEndpoint}
        resolveUsers={({ userIds }) =>
          resolveUsers({ userIds, authors: [aiAuthor, ...authors] })
        }
        resolveMentionSuggestions={({ text }) =>
          resolveMentionSuggestions({ text, authors })
        }
        preventUnsavedChanges
      >
        <CollaborationRoomInner>{children}</CollaborationRoomInner>
      </LiveblocksProvider>
    </CollaborationStateProvider>
  )
}

// We split CollaborationRoom and CollaborationRoomInner so the latter can call useClient with a matching LiveblocksProvider parent.
function CollaborationRoomInner(props: PropsWithChildren) {
  const { children } = props

  // TODO: This is a temporary solution.
  // We need to rename the shortId to userId in the future once long ids
  // are completely removed from the system.
  const { shortId } = useProfile()

  const client = useClient()

  const roomDataInitPromise = useRef<Promise<void>>()
  const [roomState, setRoomState] = useState<RoomState>()

  const init = useCallback(
    (initData: ExistingDocumentData) => {
      // Already init.
      if (roomState) return roomState

      const { version, meta } = initData

      // Rooms are split based on their document id and version id
      // except for draft rooms as we only have a single draft version per document
      const getRoomDetails = () => {
        if (version.status === "release") {
          return `${meta.id}/${version.release_id}`
        }
        if (version.status === "archived") {
          return `${meta.id}/${version.version_id}`
        }

        return meta.id
      }

      const roomDetails = getRoomDetails()

      const repository = getRepositoryName()
      const roomId = `${repository}/${roomDetails}`

      const releaseId =
        initData.version.status === "release"
          ? initData.version.release_id
          : undefined

      function getLiveblocksDocument() {
        return wrapWithLiveblocksModel(
          initData.document,
        ) as LiveObject<LsonObject>
      }

      const { room, leave } = client.enterRoom(roomId, {
        // The initialStorage should not be set when the document is published
        // or archived as we do not want to initialize or use Liveblocks storage
        // data for these, only Prismic Data
        initialStorage: isPublishedOrArchived(version.status)
          ? undefined
          : {
              document: getLiveblocksDocument(),
              title: initData.meta.title,
              status: version.status,
              releaseId: releaseId,
              shortId,
              tags: new LiveList(initData.version.tags),
              repository,
              documentId: initData.meta.id,
              commitHash: env.PRISMIC_GIT_COMMIT_HASH,
              lastModifiedDate:
                initData.version.last_modified_date.toISOString(),
            },
        autoConnect: false,
        initialPresence: {
          fieldId: undefined,
        },
      })

      function waitForRoomData() {
        use(room.waitUntilStorageReady())

        if (!initData.isFromInitialDraft) return

        // This room's storage needs to be force updated with the initial draft, in case the room already existed
        // as the initial draft should always win and replace the room data.
        roomDataInitPromise.current ||= new Promise((resolve) => {
          const unsubscribe = room.events.storageStatus.subscribe((status) => {
            if (status !== "synchronized") return
            unsubscribe()
            resolve()
          })

          room.getStorageSnapshot()?.update({
            document: getLiveblocksDocument(),
          })
        })

        use(roomDataInitPromise.current)
      }

      const newRoomState = { room, leave, waitForRoomData }

      // init() is an interesting beast. It MUST be called from a render function
      // because we want to suspend on the room's storage availability before showing any UI so we need a reference to the room synchronously.
      // However, because this is called in render, we can't set our own state synchronously, lest we get a react warning.
      // Using a state also ensures we re-render this component with a room once we have it, otherwise we would get missing room errors.
      setTimeout(() => setRoomState(newRoomState))

      return newRoomState
    },
    [client, roomState, shortId],
  )

  useEffect(() => {
    roomState?.room.connect()
    return () => roomState?.leave()
  }, [roomState])

  return (
    <LiveblocksRoomContext.Provider value={roomState?.room ?? null}>
      <initRoomContext.Provider value={init}>
        {children}
        <RoomConnectionObserver />
      </initRoomContext.Provider>
    </LiveblocksRoomContext.Provider>
  )
}

interface RoomState {
  room: Room
  waitForRoomData: () => void
  leave: () => void
}

interface UseCollaborationStorageArgs {
  /**
   * Supply the document version status directly to determine if collaboration data should be used.
   * This way we aren't dependent on URL search params or what is in the storage, as it may be empty.
   */
  status: DocumentVersionStatus
}

/**
 * Similar to Liveblock's useStorage but optional and not throwing.
 * Indeed, the presence of a room above the component hierarchy is optional for Page Builder.
 */
export function useCollaborationStorage(args: UseCollaborationStorageArgs) {
  const { status } = args

  const goToDocument = useGoToDocument()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  // TODO: This is a temporary solution.
  // We need to rename the shortId to userId in the future once long ids
  // are completely removed from the system.
  const { shortId } = useProfile()

  try {
    const value = useStorage((storageObject) => storageObject) ?? undefined

    // This is actually safe. The try catch is here in case a collaboration room is not applicable in the current feature flag context.
    // With this we ensure the client hook doesn't throw if the route params are not available.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const existingDocumentParams = useExistingDocumentParams()

    // This is actually safe. The try catch is here in case a collaboration room is not applicable in the current feature flag context.
    // With this we ensure the client hook doesn't throw if the room is not available.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const client = useClient()

    // This is actually safe. The try catch is here in case a collaboration room is not applicable in the current feature flag context.
    // If we change version and a room becomes available, this hook will be a brand new one nested inside a newly mounted CollaborationRoomInner parent.
    // If the room is not available, the first hook in the try catch will always throw. If the room is available, neither hook will ever throw.
    // Thus, that hook is not conditional.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const update = useMutation(
      async ({ storage }, localData: UpdateStorageObject) => {
        // If we can't use collaboration data, we don't want to write to rooms
        // where the document is published or archived
        if (!canUseCollaborationData && isPublishedOrArchived(status)) return

        // If we can use collaboration data, we want to create a document
        // for the current draft room if it doesn't exist already, this should
        // only happen if the document is published or archived
        //
        // The draft will be based on the current room data from LB
        if (canUseCollaborationData && isPublishedOrArchived(status)) {
          // store the current storage object from the current room
          const currentStorage = storage.toObject()
          const newRoomId = `${getRepositoryName()}/${existingDocumentParams.documentId}`

          // create a document for the new room if it doesn't exist already
          // we can't navigate to documents that aren't stored in Prismic DB
          await maybeCreateDocument({
            documentId: existingDocumentParams.documentId,
            localData,
          })

          // leave the current room
          // navigate to a new draft version of the document
          goToDocument({
            status: "unclassified",
            id: existingDocumentParams.documentId,
            replace: false,
          })

          const initialStorage: Liveblocks["Storage"] = {
            ...mergeChangesIntoStorageObject(
              { ...currentStorage, ...localData },
              true,
            ),
            commitHash: env.PRISMIC_GIT_COMMIT_HASH,
            status: "unclassified",
            lastModifiedDate: new Date().toISOString(),
            shortId,
          }

          // initialize a new room with the current storage object
          client.enterRoom(newRoomId, {
            initialStorage,
            initialPresence: {
              fieldId: undefined,
            },
          })

          // await the new room storage to be ready for us to be able to update it
          await client.getRoom(newRoomId)?.waitUntilStorageReady()

          // update the new room storage with the current storage object
          // and the new data the user tried to add to the previous room
          //
          // This workaround is needed because if the room is already initialized
          // we can't update the storage object with the new data on the enter room function
          // we have to explicitly update the storage object with the new data
          client
            .getRoom(newRoomId)
            ?.getStorageSnapshot()
            ?.update(initialStorage)

          return
        }

        // If the version is not the same as the current schema version,
        // we need to update the storage object on the next write operation
        //
        // TODO: re-evaluate this strategy once we introduce scripts for
        // cleaning rooms as we need to determine the right approach for other
        // cases, like going to a history item.
        // [PBD-2193](https://linear.app/prismic/issue/PBD-2193/create-cleanup-script-for-cleaning-liveblock-rooms)
        if (storage.get("schemaVersion") === SCHEMA_VERSION) {
          handleDocumentStorageChanges({ storage, localData })
          storage.update({
            ...mergeChangesIntoStorageObject({ ...localData, shortId }),
            lastModifiedDate: new Date().toISOString(),
            commitHash: env.PRISMIC_GIT_COMMIT_HASH,
          })
        } else {
          storage.update({
            ...mergeChangesIntoStorageObject(
              {
                ...storage.toObject(),
                ...localData, // allow any new changes to be included while updating the version
                shortId,
              },
              true, // allow document to be overwritten as the current storage object is not compatible with the new one
            ),
            lastModifiedDate: new Date().toISOString(),
            schemaVersion: SCHEMA_VERSION,
            commitHash: env.PRISMIC_GIT_COMMIT_HASH,
          })
        }
      },
      [],
    )

    // If we can't use collaboration data, we don't want to use room data
    // If the document is a draft or release, we want to use room data
    // Otherwise, we don't want to use room data and only show Prismic DB data
    const maybeValue =
      canUseCollaborationData && isDraftOrRelease(status) ? value : undefined

    return {
      value: maybeValue,
      update,
    }
  } catch {
    return undefined
  }
}

/** Internal, don't use outside ExistingDocument. */
export function useInitCollaborationRoom() {
  return useContext(initRoomContext)
}

type InitRoomContextValue = (initData: ExistingDocumentData) => RoomState

const initRoomContext = createContext<InitRoomContextValue | undefined>(
  undefined,
)

interface UseCollaborativeCommentsArgs {
  field?: string
  initialContent?: Content
}

export function useCollaborativeComments(
  args: UseCollaborativeCommentsArgs,
): Extension {
  const extension = useLiveblocksExtension({
    comments: true,
    mentions: false, // A tricky bug occurs when mentions are enabled and the content includes an '@' symbol—the editor crashes because it attempts to unregister the Mention plugin.
    ...args,
  })

  return extension
}

export interface UpdateStorageObject {
  document?:
    | Liveblocks["Storage"]["document"]
    | ExistingDocumentData["document"]
  title?: string
  status?: DocumentVersionStatus
  shortId?: string
  releaseId?: string
  tags?: LiveList<string>
  contentId?: string
  lastModifiedDate?: string
}

function mergeChangesIntoStorageObject(
  changes: UpdateStorageObject,
  overrideDocument?: boolean,
) {
  return {
    ...(overrideDocument && changes.document
      ? {
          document: wrapWithLiveblocksModel(
            changes.document,
          ) as LiveObject<LsonObject>,
        }
      : {}),
    ...(changes.title ? { title: changes.title } : {}),
    ...(changes.status ? { status: changes.status } : {}),
    ...(changes.shortId ? { shortId: changes.shortId } : {}),
    ...(changes.releaseId ? { releaseId: changes.releaseId } : {}),
    ...(changes.contentId ? { contentId: changes.contentId } : {}),
    ...(changes.tags
      ? { tags: wrapWithLiveblocksModel(changes.tags) as LiveList<string> }
      : {}),
    ...(changes.lastModifiedDate
      ? { lastModifiedDate: changes.lastModifiedDate }
      : {}),
  }
}

interface MaybeCreateDocumentArgs {
  documentId: string
  localData: UpdateStorageObject
}

async function maybeCreateDocument(args: MaybeCreateDocumentArgs) {
  const { documentId, localData } = args

  const meta = await getDocumentById(
    baseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    documentId,
  )
  // Get the most recent version of the current document
  const version = findDocumentVersion({
    meta,
    releaseId: undefined,
    status: "unclassified",
    archivedVersionId: undefined,
  })

  // Combine the version and local data
  // This is needed to transfer changes from the published document to the
  // newly created draft document
  const versionWithLocalChanges = {
    ...version,
    ...localData,
  }

  if (version.status !== "unclassified") {
    await saveDocument({
      id: meta.id,
      baseVersionId: undefined,
      releaseId: undefined,
      documentTags: new Set(versionWithLocalChanges.tags),
      content:
        versionWithLocalChanges.document as unknown as ExistingDocumentData["document"],
      status: "unclassified",
    })
  }
}

declare global {
  interface Liveblocks {
    Storage: Partial<LiveblocksStorageType> | LiveblocksStorageType

    RoomEvent:
      | { type: "autosave"; state: "pending" }
      | { type: "autosave"; state: "success" }
      | { type: "autosave"; state: "error" }

    UserMeta: {
      id?: string
      info?: {
        /**
         * Liveblocks specific field, representing the full user name.
         *
         * Must be present for TipTap plugin
         */
        name?: string
        /**
         * Liveblocks specific field, representing the attached user color.
         *
         * Must be present for TipTap plugin.
         */
        color?: string
        avatar?: string | undefined
        firstname?: string
        lastname?: string
        email?: string
        themeColor?: "indigo9" | "purple9" | "green9" | "amber9" | "grey9"
      }
    }
  }
}

type LiveblocksStorageType = {
  document: LiveObject<LsonObject>
  title: string | undefined
  status: DocumentVersionStatus
  shortId: string
  releaseId: string | undefined
  tags: LiveList<string>
  repository: string
  documentId: string
  schemaVersion: typeof SCHEMA_VERSION | undefined
  commitHash: string | undefined
  contentId: string | undefined
  lastModifiedDate: string | undefined
}

const SCHEMA_VERSION = 3

function isPublishedOrArchived(
  status: DocumentVersionStatus,
): status is "published" | "archived" {
  return status === "published" || status === "archived"
}

function isDraftOrRelease(
  status: DocumentVersionStatus,
): status is "unclassified" | "release" {
  return status === "unclassified" || status === "release"
}
